import { AI_HERO, AI_GATE_PREVIEW } from "../content";
import { AiRichText, scrollToAiAnchor } from "./AiSection";

/**
 * Hero 首屏：深色品牌底 + 径向光晕 + 网格纹理，左侧主张/数据，
 * 右侧「四道关口」预览卡。对应设计稿第 1 节。
 * 按钮为面板内锚点（平滑滚动，不改写地址栏 hash）。
 */
export default function AiHero() {
  return (
    <section
      className="relative overflow-hidden rounded-3xl text-white/80"
      style={{
        background:
          "radial-gradient(1100px 520px at 12% -10%, rgba(26,86,219,0.34), transparent 62%)," +
          "radial-gradient(900px 480px at 92% 8%, rgba(34,211,238,0.18), transparent 60%)," +
          "linear-gradient(160deg, #0b1b3d 0%, #060e1e 58%, #04080f 100%)",
      }}
    >
      {/* 网格纹理遮罩层（对应设计稿 .hero::after） */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(77,184,255,0.055) 1px, transparent 1px), linear-gradient(90deg, rgba(77,184,255,0.055) 1px, transparent 1px)",
          backgroundSize: "62px 62px",
          maskImage: "radial-gradient(circle at 50% 30%, #000 0%, transparent 78%)",
          WebkitMaskImage: "radial-gradient(circle at 50% 30%, #000 0%, transparent 78%)",
        }}
      />
      <div className="relative z-[2] px-6 lg:px-14 py-14 lg:py-20">
        <div className="grid gap-12 lg:gap-16 items-start lg:grid-cols-[1.12fr_0.88fr]">
          <div>
            {/* 徽章 */}
            <div className="flex flex-wrap gap-3 mb-8">
              {AI_HERO.badges.map((b) => (
                <span
                  key={b.text}
                  className={`inline-flex items-center gap-2 px-[15px] py-[7px] rounded-full text-xs font-semibold ${
                    b.solid
                      ? "bg-[#1a56db]/28 border border-[#1a56db]/50 text-[#cfe0ff]"
                      : "bg-[#4db8ff]/10 border border-[#4db8ff]/26 text-[#4db8ff]"
                  }`}
                >
                  <i className={`${b.icon} text-[0.95em]`}></i>
                  {b.text}
                </span>
              ))}
            </div>

            {/* 主张（页面 h1 由站点级 SolutionsHero 提供，此处用 h2 避免同页双 h1） */}
            <h2 className="text-3xl lg:text-5xl font-bold tracking-tight leading-tight text-white">
              {AI_HERO.titleLine1Pre}
              <span className="bg-gradient-to-r from-[#4db8ff] via-[#60a5fa] to-[#22d3ee] bg-clip-text text-transparent">
                {AI_HERO.titleLine1Highlight}
              </span>
              <br />
              {AI_HERO.titleLine2}
            </h2>

            <AiRichText
              segments={AI_HERO.lead}
              onDark
              className="mt-6 text-sm lg:text-base leading-relaxed text-white/80 max-w-3xl [&>strong]:text-white"
            />

            {/* 行动按钮（面板内锚点平滑滚动） */}
            <div className="flex flex-wrap gap-4 mt-10">
              <button
                onClick={() => scrollToAiAnchor("ai-contact")}
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#1a56db] text-white text-sm font-semibold shadow-[0_10px_28px_rgba(26,86,219,0.22)] hover:bg-[#1549bd] hover:-translate-y-0.5 transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-calendar-check-line"></i>
                预约 AI 安全评估
              </button>
              <button
                onClick={() => scrollToAiAnchor("ai-architecture")}
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-white/8 border border-white/12 text-white text-sm font-semibold backdrop-blur-[6px] hover:bg-white/16 hover:-translate-y-0.5 transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-node-tree"></i>
                查看方案架构
              </button>
            </div>

            {/* 数据 */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mt-12 lg:mt-16 pt-8 lg:pt-10 border-t border-white/12">
              {AI_HERO.stats.map((s) => (
                <div key={s.label}>
                  <div className="font-mono text-2xl lg:text-[2rem] font-bold text-white tracking-tight">
                    {s.num}
                    <em className="not-italic text-[0.6em] text-[#4db8ff] ml-0.5">{s.unit}</em>
                  </div>
                  <div className="text-xs text-white/55 mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* 四道关口预览卡 */}
          <aside
            aria-label="AI 数据安全四道关口"
            className="rounded-3xl border border-white/12 p-6 shadow-[0_18px_40px_rgba(0,0,0,0.35)] backdrop-blur-[10px]"
            style={{ background: "linear-gradient(165deg, rgba(255,255,255,0.075), rgba(255,255,255,0.025))" }}
          >
            <div className="flex items-center justify-between pb-4 mb-5 border-b border-white/12">
              <span className="text-xs font-bold tracking-[0.12em] uppercase text-[#4db8ff]">{AI_GATE_PREVIEW.title}</span>
              <span className="inline-flex items-center gap-2 text-xs text-white/55">
                <span className="w-[7px] h-[7px] rounded-full bg-[#059669] animate-pulse"></span>
                {AI_GATE_PREVIEW.liveLabel}
              </span>
            </div>
            <ul className="grid gap-3 m-0 p-0 list-none">
              {AI_GATE_PREVIEW.items.map((g) => (
                <li
                  key={g.name}
                  className="grid grid-cols-[34px_minmax(0,1fr)_auto] items-center gap-4 p-4 rounded-[10px] bg-white/5 border border-white/12 hover:bg-[#4db8ff]/10 hover:border-[#4db8ff]/30 transition-colors duration-200"
                >
                  <span className="w-[34px] h-[34px] rounded-md grid place-items-center text-base bg-[#1a56db]/30 text-[#4db8ff]">
                    <i className={g.icon}></i>
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-bold text-white">{g.name}</span>
                    <span className="block text-xs text-white/55 leading-relaxed">{g.desc}</span>
                  </span>
                  <span className="font-mono text-[11px] font-bold px-2 py-[3px] rounded-full bg-[#059669]/16 text-[#34d399] border border-[#059669]/32 whitespace-nowrap">
                    {g.level}
                  </span>
                </li>
              ))}
            </ul>
            <AiRichText
              segments={AI_GATE_PREVIEW.foot}
              onDark
              className="mt-5 pt-4 border-t border-white/12 text-xs leading-relaxed text-white/55 [&>strong]:text-[#4db8ff]"
            />
          </aside>
        </div>
      </div>
    </section>
  );
}
